import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { ROOTS } from 'src/routes/paths';
import { CONFIG } from 'src/config-global';
import { POLICY } from 'src/utils/constants';
import { navData } from 'src/layouts/config-nav-dashboard';
import { DashboardLayout } from 'src/layouts/dashboard';
import { LoadingScreen } from 'src/components/loading-screen';
import { AuthGuard } from 'src/auth/guard';
import AccessGuard from '../../auth/guard/access-guard';

const layoutContent = (
  <DashboardLayout data={{ nav: navData }}>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

const Loadable =
  (Component) =>
  ({ policies, ...props }) => (
    <Suspense fallback={<LoadingScreen />}>
      <AccessGuard policies={policies}>
        <Component {...props} />
      </AccessGuard>
    </Suspense>
  );

const IndexPage = lazy(() => import('src/pages/dashboard'));
const GamesConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration/list'))
);

const ExternalGamesConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration-external/list'))
);

const BonusBuyConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/feature-configuration/bonus-buy/list'))
);
const AnteBetConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/feature-configuration/ante-bet/list'))
);

const OperatorsListPage = Loadable(lazy(() => import('src/pages/dashboard/operators/list')));
const OperatorDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/operators/details/details'))
);

const GamesConfigurationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration/details/details'))
);

const ClientsConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/client-configuration/list'))
);

const ClientsConfigurationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/client-configuration/details/details'))
);
const UserListPage = Loadable(lazy(() => import('src/pages/dashboard/user/list')));
const RolesListPage = Loadable(lazy(() => import('src/pages/dashboard/roles/list')));

const LPConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/jackpot-configuration/liquidity-pools/list'))
);
const ProgressiveJackpotsListPage = Loadable(
  lazy(() => import('src/pages/dashboard/jackpot-configuration/progressive-jackpots/list'))
);
const ProgressiveJackpotsDetailsPage = Loadable(
  lazy(
    () => import('src/pages/dashboard/jackpot-configuration/progressive-jackpots/details/details')
  )
);
const GroupsListPage = Loadable(
  lazy(() => import('src/pages/dashboard/jackpot-configuration/groups/list'))
);
const GroupDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/jackpot-configuration/groups/details/details'))
);
const LoaderPage = Loadable(lazy(() => import('src/pages/dashboard/loader/loader')));

const OsListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-history/open-sessions/list'))
);

const GameRoundHistoryListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-history/game-round-history/list'))
);

const RtpVerificationPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/rtpVerification/list'))
);

const RtpVerificationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/rtpVerification/details/details'))
);
export const dashboardRoutes = [
  {
    path: ROOTS.DASHBOARD,
    element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'user',
        children: [
          { element: <UserListPage />, index: true },
          { path: 'list', element: <UserListPage /> },
          { path: 'roles', element: <RolesListPage /> },
        ],
      },
      {
        path: 'games-configuration',
        children: [
          {
            element: <GamesConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
            index: true,
          },
          {
            path: 'list',
            element: <GamesConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          {
            path: 'external',
            element: <ExternalGamesConfigurationListPage policies={[POLICY.GAME_CONF_GET_EXT]} />,
          },
          {
            path: ':id',
            element: <GamesConfigurationDetailsPage policies={[POLICY.GAME_CONF_CREATE_UPDATE]} />,
          },
        ],
      },
      {
        path: 'feature-configuration',
        children: [
          {
            element: <BonusBuyConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
            index: true,
          },
          {
            path: 'bonus-buy',
            element: <BonusBuyConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          {
            path: 'ante-bet',
            element: <AnteBetConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          // {
          //   path: ':id',
          //   element: <GamesConfigurationDetailsPage policies={[POLICY.GAME_CONF_CREATE_UPDATE]} />,
          // },
        ],
      },
      {
        path: 'client-configuration',
        children: [
          {
            element: <ClientsConfigurationListPage policies={[POLICY.CLIENT_CONF_GET]} />,
            index: true,
          },
          {
            path: 'list',
            element: <ClientsConfigurationListPage policies={[POLICY.CLIENT_CONF_GET]} />,
          },
          {
            path: ':id',
            element: (
              <ClientsConfigurationDetailsPage policies={[POLICY.CLIENT_CONF_CREATE_UPDATE]} />
            ),
          },
          {
            path: 'loader',
            element: <LoaderPage />,
          },
        ],
      },
      {
        path: 'jackpot-configuration',
        children: [
          {
            path: 'liquidity-pools',
            children: [
              { element: <LPConfigurationListPage />, index: true },
              { path: 'list', element: <LPConfigurationListPage /> },
            ],
          },
          {
            path: 'list',
            element: <ProgressiveJackpotsListPage />,
          },
          {
            path: ':id',
            element: <ProgressiveJackpotsDetailsPage />,
          },
          {
            path: 'groups',
            children: [
              { element: <GroupsListPage />, index: true },
              { path: 'list', element: <GroupsListPage /> },
              {
                path: ':id',
                element: <GroupDetailsPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'game-history',
        children: [
          {
            path: 'opened-sessions',
            children: [
              { element: <OsListPage />, index: true },
              { path: 'list', element: <OsListPage /> },
            ],
          },
          {
            path: 'game-round-history',
            children: [
              { element: <GameRoundHistoryListPage />, index: true },
              { path: 'list', element: <GameRoundHistoryListPage /> },
            ],
          },
        ],
      },
      {
        path: 'testing',
        children: [
          {
            path: 'rtp-verification',
            children: [
              { element: <RtpVerificationPage />, index: true },
              { path: 'list', element: <RtpVerificationPage /> },
              {
                path: ':id',
                element: <RtpVerificationDetailsPage />,
              },
            ],
          },
        ],
      },

      // {
      //   path: 'gm/binaries',
      //   children: [
      //     { element: <BinariesListPage />, index: true },
      //     { path: 'list', element: <BinariesListPage /> },
      //   ],
      // },
      // {
      //   path: 'gm/engines',
      //   children: [
      //     { element: <EnginesListPage />, index: true },
      //     { path: 'list', element: <EnginesListPage /> },
      //   ],
      // },
      {
        path: 'operators',
        children: [
          { element: <OperatorsListPage />, index: true },
          { path: 'list', element: <OperatorsListPage /> },
          {
            path: ':id',
            element: <OperatorDetailsPage />,
          },
        ],
      },
    ],
  },
];
